import { joinFields } from './normalize';

export type RejectionReason =
  | 'capacity_or_closed'
  | 'sold_out'
  | 'closed'
  | 'email_invalid_or_taken'
  | 'age_limit'
  | 'gender_class'
  | 'team_name_taken'
  | 'team_size'
  | 'custom_field'
  | 'class_or_wave'
  | 'payment_method_unavailable'
  | 'giftcard_invalid'
  | 'magic_link_invalid'
  | 'product_invalid'
  | 'terms_missing'
  | 'captcha_failed'
  | 'pace_shield'
  | 'login_required'
  | 'forbidden'
  | 'rate_limited'
  | 'other_422'
  | 'other_4xx';

/**
 * Ordered matchers on backend error keys (keys are not translated, messages are).
 * The first matching key in the response decides the reason, the order here decides ties.
 */
const KEY_MATCHERS: Array<[RegExp, RejectionReason]> = [
  [/^registrations\.\d+\.distance_id$/, 'capacity_or_closed'],
  [/(^|\.)distance_id$/, 'capacity_or_closed'],
  [/^turnstile_token$/, 'captcha_failed'],
  [/(^|\.)payment_method$/, 'payment_method_unavailable'],
  [/(^|\.)pay_onsite$/, 'payment_method_unavailable'],
  [/(^|\.)giftcard/, 'giftcard_invalid'],
  [/magic_link/, 'magic_link_invalid'],
  [/(^|\.)products?(\.|$)/, 'product_invalid'],
  [/terms/, 'terms_missing'],
  [/(^|\.)refund_protect$/, 'pace_shield'],
  [/(^|\.)team\.name$/, 'team_name_taken'],
  [/(^|\.)team\.members$/, 'team_size'],
  [/(^|\.)birthday$/, 'age_limit'],
  [/(^|\.)gender$/, 'gender_class'],
  [/(^|\.)email$/, 'email_invalid_or_taken'],
  [/(^|\.)custom(\.|$)/, 'custom_field'],
  [/(^|\.)(class_id|wave_id|discipline_id)$/, 'class_or_wave'],
];

/**
 * @description
 * Pick one reason code for a rejected registration submit and list the normalized fields involved
 */
export function reasonFromErrors(status: number | nil, errors: RawError | nil): { reason: RejectionReason; fields: string } {
  const keys = errors ? Object.keys(errors) : [];
  const fields = joinFields(keys);

  if (status === 401) return { reason: 'login_required', fields };
  if (status === 429) return { reason: 'rate_limited', fields };

  for (const [pattern, reason] of KEY_MATCHERS) {
    if (keys.some((key) => pattern.test(key))) {
      return { reason, fields };
    }
  }

  if (status === 403) return { reason: 'forbidden', fields };
  if (status === 422) return { reason: 'other_422', fields };

  return { reason: 'other_4xx', fields };
}

/**
 * @description
 * After a capacity_or_closed rejection the race and distance are reloaded,
 * the reloaded state tells which of the two it was
 */
export function refineCapacityReason(isSoldOut: boolean, isClosed: boolean): RejectionReason {
  if (isSoldOut) return 'sold_out';
  if (isClosed) return 'closed';
  return 'capacity_or_closed';
}
